import dayjs from "dayjs";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

interface GlobalState {
  pageTitle: string;
  setPageTitle: (title: string) => void;
  passwordChangeNecessary: boolean;
  setPasswordChangeNecessary: (pcs: boolean) => void;
  getDate: () => dayjs.Dayjs;

  date: string;
  setDate: (date: dayjs.Dayjs) => void;
}

interface PersistantState {
  authenticated: boolean;
  setAuthenticated: (auth: boolean, userId?: number) => void;
  region: number;
  setRegion: (region: number) => void;
  userId: number;
  transferPlace: number;
  setTransferPlace: (place: number) => void;
  plantReference: string | null;
  setPlantReference: (region: string) => void;
  kgpReference: number;
  setKgpReference: (region: number) => void;
  panels: (string | null)[];
  setPanels: (index: number, key: string) => void;
  getSettings: () => any;
  dispoCategory: number;
  setDispoCategory: (dispoCategory: number) => void;
  type: any;
  setType: (type: any) => void;
  reference: any;
  setReference: (reference: any) => void;
  settings: any;
  setSettings: (reference: any) => void;
}

export const useGlobalStore = create<GlobalState>()(
  devtools(
    (set, get) => ({
      pageTitle: "",
      setPasswordChangeNecessary: (passwordChangeNecessary) => set((state) => ({ passwordChangeNecessary })),
      passwordChangeNecessary: false,
      setPageTitle: (pageTitle) => set((state) => ({ pageTitle })),
      getDate: () => dayjs(get().date),
      date: dayjs().add(1, "day").format("YYYY-MM-DD"),
      setDate: (date) => set((state) => ({ date: date.format("YYYY-MM-DD") })),
    }),
    {
      name: "global-storage",
    },
  ),
);

const defaultSettings = {
  showCrewMembers: false,
  showDispoResources: true,
  dispoExpanded: true,
  showDispoOrders: true,
  showDispoInformation: true,
  showDispoMaterial: true,
  showCrewResources: false,
  showOtherStaff: false,
  showOtherResources: false,
  showMaterial: false,
  showAutobookings: false,
  expertMode: false,
  moveOnlyResource: false,
  showLogistics: false,
  sortDispoBy: "NAME",
  sideBarHidden: false,
  showDispoBauhof: false,
  calendarType: "VERTICAL",
  hcIncludeCrews: true,
  hcIncludeCrewMembers: false,
  hcIncludeHumans: true,
  hcIncludeResources: true,
  hcIncludeNotes: true,
};

export const usePersistantStore = create<PersistantState>()(
  devtools(
    persist(
      (set, get) => ({
        region: 0,
        setRegion: (region) => set((state) => ({ region })),
        userId: 0,
        transferPlace: 0,
        setTransferPlace: (transferPlace) => set({ transferPlace }),
        plantReference: null,
        dispoCategory: 0,
        panels: [],
        setPanels: (index, key) =>
          set((state) => {
            const panels = [...state.panels];

            const existingIndex = panels.indexOf(key);
            if (existingIndex !== -1) {
              panels[existingIndex] = null;
            }

            while (panels.length <= index) {
              panels.push(null);
            }

            panels[index] = key;

            return { panels };
          }),
        kgpReference: 0,
        setKgpReference: (kgpReference) => set((state) => ({ kgpReference })),
        type: null,
        reference: null,
        settings: defaultSettings,
        getSettings: () => ({ ...defaultSettings, ...get().settings }),
        setSettings: (settings) => set((state) => ({ settings: { ...state.settings, ...settings } })),
        setType: (type) => set((state) => ({ type })),
        setReference: (reference) => set((state) => ({ reference })),
        setPlantReference: (plantReference) => set((state) => ({ plantReference })),

        setDispoCategory: (dispoCategory) => set((state) => ({ dispoCategory })),
        authenticated: false,
        setAuthenticated: (authenticated, userId = 0) => {
          let reset = {};
          if (!authenticated) {
            localStorage.setItem(import.meta.env.VITE_APP_NAME + "-token", "");
            localStorage.setItem(import.meta.env.VITE_APP_NAME + "-refreshToken", "");
            localStorage.setItem(import.meta.env.VITE_APP_NAME + "-rights", "");
          } else if (get().userId !== userId) {
            reset = {
              settings: defaultSettings,
              region: 0,
              transferPlace: 0,
              plantReference: 0,
              dispoCategory: 0,
              type: null,
              reference: null,
            };
          }
          return set((state) => ({ authenticated, userId, ...reset }));
        },
      }),
      {
        name: "persistant-storage",
      },
    ),
  ),
);
